import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchOccasions, deleteOccasion, searchOccasions, bulkDeleteOccasions, bulkUpdateOccasionStatus } from "./occasionSlice";
import Swal from 'sweetalert2';
import ConfirmationModal from "../../components/ConfirmationModal";
import Loader from "../../components/Loader";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import log from "../../logger";
import ReactTable from "../../components/ReactTable";
import { paths } from "../../routes/helpers";
import { getImageUrl } from "../../utils/urlUtils";
import useDebounce from "../../hooks/useDebounce";

const OccasionList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { items, loading, error, total, pages } = useSelector(
    (state) => state.occasion
  );

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedOccasion, setSelectedOccasion] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentLimit, setCurrentLimit] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 1000);

  useEffect(() => {
    if (debouncedSearchTerm) {
      dispatch(searchOccasions({ keyword: debouncedSearchTerm, page: currentPage, limit: currentLimit }));
    } else {
      dispatch(fetchOccasions({ page: currentPage, limit: currentLimit }));
    }
  }, [dispatch, currentPage, currentLimit, debouncedSearchTerm]);

  const handleDelete = (occasionId) => {
    setShowDeleteModal(true);
    setSelectedOccasion(occasionId);
  };

  const confirmDelete = async () => {
    if (selectedOccasion) {
      try {
        await dispatch(deleteOccasion(selectedOccasion)).unwrap();
        toast.success("Occasion deleted successfully!");
        log.info("Occasion deleted:", selectedOccasion);
      } catch (err) {
        toast.error(err || "Failed to delete occasion");
        log.error("Error deleting occasion:", err);
      } finally {
        setShowDeleteModal(false);
        setSelectedOccasion(null);
      }
    }
  };

  const handleEdit = (occasionId) => {
    navigate(paths.private.occasion.edit(occasionId));
  };

  const handleCreateOccasion = () => {
    navigate(paths.private.occasion.create());
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleLimitChange = (newLimit) => {
    setCurrentLimit(newLimit);
    setCurrentPage(1);
    if (debouncedSearchTerm) {
      dispatch(searchOccasions({ keyword: debouncedSearchTerm, page: 1, limit: newLimit }));
    } else {
      dispatch(fetchOccasions({ page: 1, limit: newLimit }));
    }
  };

  const handleSearch = (searchValue) => {
    setSearchTerm(searchValue);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleBulkDelete = async (ids, clearSelection) => {
    const result = await Swal.fire({
      title: `Delete ${ids.length} occasion${ids.length > 1 ? 's' : ''}?`,
      text: 'This action cannot be undone!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;
    try {
      await dispatch(bulkDeleteOccasions(ids)).unwrap();
      Swal.fire({ title: 'Deleted!', text: `${ids.length} occasion${ids.length > 1 ? 's' : ''} deleted.`, icon: 'success', timer: 1500, showConfirmButton: false });
    } catch (err) {
      Swal.fire({ title: 'Some deletions failed', text: err || 'One or more items are still in use elsewhere and were not deleted.', icon: 'error' });
    } finally {
      clearSelection();
    }
  };

  const handleBulkStatusChange = (status) => async (ids, clearSelection) => {
    const activating = status === 1;
    const result = await Swal.fire({
      title: `${activating ? 'Activate' : 'Deactivate'} ${ids.length} occasion${ids.length > 1 ? 's' : ''}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: `Yes, ${activating ? 'activate' : 'deactivate'}`,
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;

    dispatch(bulkUpdateOccasionStatus({ ids, status })).then(() => {
      Swal.fire({ title: 'Updated!', text: `${ids.length} occasion${ids.length > 1 ? 's' : ''} ${activating ? 'activated' : 'deactivated'}.`, icon: 'success', timer: 1500, showConfirmButton: false });
      clearSelection();
    });
  };

  const formatTableData = (occasions) => {
    return occasions.map((occasion) => ({
      _id: occasion._id,
      label: occasion.label || '-',
      image: getImageUrl(occasion.image) || null,
      status: occasion.status ? 'Active' : 'Inactive',
      createdAt: occasion.createdAt ? new Date(occasion.createdAt).toLocaleString() : '-',
      updatedAt: occasion.updatedAt ? new Date(occasion.updatedAt).toLocaleString() : '-',
    }));
  };

  const headers = [
    { label: 'Image', key: 'image' },
    { label: 'Label', key: 'label' },
    { label: 'Status', key: 'status' },
    { label: 'Created At', key: 'createdAt' },
    { label: 'Updated At', key: 'updatedAt' },
  ];

  const actions = [
    { label: 'Edit', onClick: handleEdit, color: 'blue' },
    { label: 'Delete', onClick: handleDelete, color: 'red' },
  ];

  if (loading && items.length === 0) return (
    <div className="p-4">
      <Loader />
    </div>
  );

  if (error) return (
    <div className="p-4">
      <div className="text-red-500">Error: {error}</div>
    </div>
  );

  return (
    <div className="p-4">
      <h1 className="text-xl font-bold mb-4">Occasions</h1>
      <div className="flex justify-between items-center mb-4">
        <button
          onClick={handleCreateOccasion}
          className="bg-green-500 text-white px-4 py-2 rounded"
        >
          Create New Occasion
        </button>
        <div className="text-sm text-gray-600">
          {searchTerm ? `Found ${total} occasions` : `Showing ${items.length} of ${total} occasions`}
        </div>
      </div>

      {/* Always show the table for search functionality */}
      <ReactTable
        headers={headers}
        data={formatTableData(items)}
        actions={actions}
        onSearch={handleSearch}
        searchDelay={1000}
        searchValue={searchTerm}
        loading={loading}
        pagination={items.length > 0 ? { page: currentPage, limit: currentLimit, total, pages } : null}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
        enableRowSelection={true}
        bulkActions={[
          { label: 'Activate Selected', color: 'green', onClick: handleBulkStatusChange(1) },
          { label: 'Deactivate Selected', color: 'gray', onClick: handleBulkStatusChange(0) },
          { label: 'Delete Selected', color: 'red', onClick: handleBulkDelete },
        ]}
      />

      {/* Show no results message only if there are no items */}
      {items.length === 0 && (
        <div className="text-center py-8 bg-gray-50 rounded-lg mt-4">
          <p className="text-gray-500">
            {searchTerm ? `No occasions found for "${searchTerm}"` : 'No occasions found'}
          </p>
          <p className="text-gray-400 text-sm mt-2">
            {searchTerm ? 'Try a different search term' : 'Click the Create New button to add one'}
          </p>
        </div>
      )}

      {showDeleteModal && (
        <ConfirmationModal
          message="Are you sure you want to delete this occasion?"
          onConfirm={confirmDelete}
          onCancel={() => setShowDeleteModal(false)}
        />
      )}
    </div>
  );
};

export default OccasionList;
